import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FireSimple, ArrowClockwise } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { shareOrigin } from "../lib/shareOrigin";
import { buildIncidentCaption, evacsForShare } from "../lib/shareCaption";
import InlineShareButton from "../components/InlineShareButton";
import { relTime } from "../lib/incidents";

export default function IncidentSummary() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inc, setInc] = useState(null);
  const [evacAlerts, setEvacAlerts] = useState([]);
  const [lastSharedAt, setLastSharedAt] = useState(null);

  useEffect(() => {
    api.get(`/incidents/${id}`).then((r) => setInc(r.data)).catch(() => {});
    api.get("/evac-alerts").then((r) => setEvacAlerts(r.data || [])).catch(() => {});
    api.get(`/metrics/share-log/${id}`)
      .then((r) => setLastSharedAt(r.data.last_shared_at || null))
      .catch(() => {});
  }, [id]);

  const load = (refresh = false) => {
    setLoading(true);
    setError(null);
    api
      .get(`/incidents/${id}/ai-summary${refresh ? "?refresh=true" : ""}`, { timeout: 90000 })
      .then((r) => setData(r.data))
      .catch(() => setError("Couldn't generate the summary — try again in a moment."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  return (
    <div className="min-h-screen bg-tac-bg text-tac-text px-4 py-10">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 text-tac-primary mb-1">
          <FireSimple size={16} weight="fill" />
          <span className="text-[12px] font-bold uppercase tracking-[0.2em]">
            AI Incident Summary
          </span>
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-1" data-testid="summary-title">
          {data?.name || "Incident"}
        </h1>
        {data?.created_at && (
          <div className="text-[13px] font-mono-tac text-tac-faint mb-6">
            Generated {relTime(data.created_at)} · AI-written from live dispatch, NIFC, evacuation
            and field data — verify critical details with official sources.
          </div>
        )}

        {loading && (
          <div className="border border-tac-border bg-tac-surface/40 p-6 text-sm text-tac-dim animate-pulse" data-testid="summary-loading">
            Reading dispatch logs, perimeter data, evacuations and field updates…
          </div>
        )}
        {error && (
          <div className="border border-tac-danger/50 bg-tac-danger/10 p-4 text-sm text-tac-danger" data-testid="summary-error">
            {error}
          </div>
        )}
        {!loading && data?.summary && (
          <div className="border border-tac-border bg-tac-surface/40 p-6" data-testid="summary-body">
            {data.summary.split(/\n{1,2}/).filter(Boolean).map((p, i) => (
              <p key={i} className="text-[15px] leading-relaxed text-tac-text mb-4 last:mb-0">
                {p}
              </p>
            ))}
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <InlineShareButton
            testid="summary-share-button"
            url={`${shareOrigin()}/api/share/incident/${id}`}
            title={`${data?.name || "Incident"} — Firewatch`}
            caption={buildIncidentCaption({
              name: data?.name || "Incident",
              county: inc?.county,
              acres: data?.acres,
              statusLabel: inc?.status_label,
              incNum: data?.inc_num,
              resources: data?.resources,
              evacs: evacsForShare(evacAlerts, inc?.latitude, inc?.longitude, data?.name),
              lastSharedAt,
              url: `${shareOrigin()}/api/share/incident/${id}`,
              summary: data?.summary,
              onlyNewEvacs: true,
            })}
            text={`${data?.name || "Incident"} — live incident details via Okanogan Highlands Firewatch`}
          />
          <button
            type="button"
            data-testid="summary-regenerate"
            onClick={() => load(true)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-bold uppercase tracking-wider border border-tac-border rounded-sm text-tac-dim hover:text-tac-text hover:border-tac-borderBright transition-colors disabled:opacity-50"
          >
            <ArrowClockwise size={13} weight="bold" /> Regenerate
          </button>
          <Link
            to={`/incident/${id}`}
            data-testid="summary-open-incident"
            className="text-[12px] font-bold uppercase tracking-wider text-tac-primary hover:underline"
          >
            Full incident page →
          </Link>
        </div>
      </div>
    </div>
  );
}
